import type { DeviceSSHKey } from '@emma/api_client/types';
import { IconButton, Text, Tooltip } from '@lego/klik-ui';
import { BanSolid, StatusSuccessBold, Trash } from '@lego/klik-ui/icons';
import type { MouseEvent } from 'react';
import { PlaceholderText } from '@/components/common/PlaceholderText';
import { UserNameLink } from '@/components/common/UserNameLink';
import { dateTimeFormat } from '@/utility/date';
import { sort } from '@/utility/sort';
import { createColumnHelper } from '../appTable';
import { dateRange } from '../filters/filterFunctions';

const columnHelper = createColumnHelper<DeviceSSHKey>();

export const sshKeysTableColumns = (onDelete?: (sshKeyId: number) => Promise<void>) =>
  columnHelper.columns([
    columnHelper.accessor('id', {
      cell: (info) => (
        <Text fontFamily="mono" fontSize="sm">
          {info.getValue()}
        </Text>
      ),
      filterFn: 'includesString',
      header: 'ID',
      meta: { filterVariant: { constraint: /^[0-9]*$/, type: 'singleSearch' } },
      size: 96,
    }),
    columnHelper.accessor('isOnDevice', {
      cell: (info) => {
        const value = info.getValue();

        if (!value)
          return (
            <Tooltip label={'The SSH key is not currently on the device'}>
              <BanSolid color="grey" />
            </Tooltip>
          );
        return (
          <Tooltip label={'The SSH key is currently on the device'}>
            <StatusSuccessBold color="green" />
          </Tooltip>
        );
      },
      filterFn: 'equals',
      header: 'Is on device',
      meta: {
        filterVariant: {
          type: 'singleSelect',
          options: [
            { name: 'On Device', filterValue: true },
            { name: 'Not On Device', filterValue: false },
          ],
        },
      },
    }),
    columnHelper.accessor('expiresAt', {
      cell: (info) => {
        const value = info.getValue();
        return value ? (
          new Date(value).toLocaleString(dateTimeFormat.local)
        ) : (
          <PlaceholderText>N/A</PlaceholderText>
        );
      },
      filterFn: dateRange,
      header: 'Expires At',
      meta: { filterVariant: { type: 'dateRange' } },
      sortFn: ({ original: { expiresAt: a } }, { original: { expiresAt: b } }) => sort(a, b),
    }),
    columnHelper.accessor('createdAt', {
      cell: (info) => {
        const value = info.getValue();
        return value ? (
          new Date(value).toLocaleString(dateTimeFormat.local)
        ) : (
          <PlaceholderText>N/A</PlaceholderText>
        );
      },
      filterFn: dateRange,
      header: 'Created At',
      meta: { filterVariant: { type: 'dateRange' } },
      sortFn: ({ original: { createdAt: a } }, { original: { createdAt: b } }) => sort(a, b),
    }),
    columnHelper.accessor('requesterOid', {
      cell: (info) => <UserNameLink oid={info.getValue()} />,
      header: 'Requester',
    }),
    columnHelper.display({
      id: 'actions',
      header: '',
      size: 50,
      cell: ({ row }) => {
        const isOnDevice = row.original.isOnDevice;

        return (
          <Tooltip label={isOnDevice ? 'Remove SSH key from device' : 'Key is not on the device'}>
            <IconButton
              aria-label="Delete SSH key"
              icon={<Trash />}
              isDisabled={!isOnDevice || !onDelete}
              onClick={(e: MouseEvent) => {
                e.stopPropagation();
                void onDelete?.(row.original.id);
              }}
              size="sm"
              variant="ghost"
            />
          </Tooltip>
        );
      },
    }),
  ]);
